/* eslint-disable max-lines */
import { NewPremiumTierSvg } from '@/assets/image/free-to-pay/new-premium-tier-svg';
import { NewProTierSvg } from '@/assets/image/free-to-pay/new-pro-tier-svg';
import { useWidgetOnboarding } from '@/hooks/integrations/useWidgetOnboarding';
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import { useMutateUpgradePlan } from '@/hooks/mutation/useMutateUpgradePlan';
import {
  getDisplayedPriceFromPlan,
  getPlanFromPeriod,
  getTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useIsUserRegionSameAsKindeRegion } from '@/hooks/useIsUserRegionSameAsKindeRegion';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { getStripeReturnRedirect, stripeCallbackRedirect } from '@/utils/scribeHTTP';
import { useDurationText } from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import { widgetLinkAccountStartTrialClicked } from '@heidi/commerce/data';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@heidi/design-system/accordion';
import { Button } from '@heidi/design-system/button';
import { Card } from '@heidi/design-system/card';
import { Skeleton } from '@heidi/design-system/skeleton';
import {
  TypographyCaption,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import { integrationNameAtom } from '@heidi/integrations/ehr/data/atoms';
import { useTranslations } from '@heidi/runtime/i18n';
import { StripeFreeTrialDays, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { Check } from 'lucide-react';
import { Trans } from 'react-i18next';

interface UserTiersProps {
  userTier: SubscriptionTier | undefined;
  period: Period.ONE_MONTH | Period.ONE_YEAR;
  onDismiss?: () => void;
  tierToUpgrade: SubscriptionTier.PRO | SubscriptionTier.PRO_PLUS;
}

const IntegrationTierAccordion = ({
  userTier = SubscriptionTier.FREE,
  period,
  tierToUpgrade,
  onDismiss,
}: UserTiersProps) => {
  const { t } = useTranslations();
  const { data: userData } = useQueryUser((data) => {
    return {
      free_trial_history_tiers: data.free_trial_history_tiers,
      email: data.email,
      kinde_user_id: data.kinde_user_id,
    };
  });
  const { data: inReverseTrial } = useQueryUserBillingInfo(
    (data) => data?.is_in_reverse_free_trial
  );

  const { userRegionSameAsKindeRegion } = useIsUserRegionSameAsKindeRegion();

  const integrationName = useAtomValue(integrationNameAtom);

  const freeTrialAvailable =
    userData?.free_trial_history_tiers?.includes(tierToUpgrade) === false;

  const { unlinkAndSwitchAccounts } = useWidgetOnboarding();

  const { mutate: createCheckoutRequest } = useMutateCheckoutV2();
  const { mutate: upgradePlan, isPending: isUpgrading } = useMutateUpgradePlan();

  const periodDisplay = (period: Period) => {
    switch (period) {
      case Period.ONE_MONTH:
        return 'monthly';
      case Period.ONE_YEAR:
        return 'annually';
      default:
        return 'per seat';
    }
  };

  const onCheckout = ({
    priceTier,
    period,
  }: {
    priceTier: SubscriptionTier.PRO | SubscriptionTier.PRO_PLUS;
    period: Period.ONE_MONTH | Period.ONE_YEAR;
  }) => {
    const freeTrialDays = freeTrialAvailable
      ? StripeFreeTrialDays.Value30
      : StripeFreeTrialDays.Value0;

    if (priceTier === SubscriptionTier.PRO) {
      createCheckoutRequest({
        plan_period: period,
        fe_endpoint: stripeCallbackRedirect,
        fe_return_endpoint: getStripeReturnRedirect(),
        tier: priceTier,
        // Note: We need to account for a bug since after the Pro reverse trial, the user doesn't have the PRO trial listed in their `free_trial_history_tiers`, so we can't use `freeTrialDays`
        // If the user sees this modal, they should have used their PRO trial, so we can assume that they will need to pay for the PRO plan in full
        free_trial_days: StripeFreeTrialDays.Value0,
        kindeUserId: userData?.kinde_user_id ?? undefined,
      });

      return;
    }

    if (priceTier === SubscriptionTier.PRO_PLUS) {
      if (userTier === SubscriptionTier.PRO && !inReverseTrial) {
        void upgradePlan({
          plan_period: period,
          tier: SubscriptionTier.PRO_PLUS,
          free_trial_days: freeTrialDays,
        });
        return;
      }

      createCheckoutRequest({
        plan_period: period,
        fe_endpoint: stripeCallbackRedirect,
        fe_return_endpoint: getStripeReturnRedirect(),
        tier: priceTier,
        kindeUserId: userData?.kinde_user_id ?? undefined,
        free_trial_days: freeTrialDays,
      });

      return;
    }

    const unhandled: never = priceTier;
    throw new Error('Invalid upgrade tier', unhandled);
  };

  const startTrialMessage = t('upgradeModal.startTrialMessage', {
    days: String(30),
  });

  if (isAppleCompliantBuild()) return null;

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={tierToUpgrade}
      className="flex max-h-[50dvh] flex-col gap-y-4 sm:max-h-fit"
    >
      {userRegionSameAsKindeRegion && (
        <Card
          id="pro-plus-tier-box"
          className={cn(
            'cursor-pointer p-0.5 hover:shadow-md',
            tierToUpgrade === SubscriptionTier.PRO_PLUS && 'bg-tier-practice',
            tierToUpgrade === SubscriptionTier.PRO && 'bg-tier-pro'
          )}
        >
          <div className="flex items-start justify-between rounded-md bg-surface p-3">
            <div className="flex w-full items-start gap-x-2">
              {tierToUpgrade === SubscriptionTier.PRO_PLUS && (
                <NewPremiumTierSvg alt="pro-plus-icon" width={32} height={32} />
              )}
              {tierToUpgrade === SubscriptionTier.PRO && (
                <NewProTierSvg alt="pro-icon" width={32} height={32} />
              )}
              <div className="flex w-full flex-col gap-y-2">
                <div className="flex items-start justify-between gap-x-0.5">
                  <div>
                    <div className="flex items-center gap-x-1">
                      {tierToUpgrade === SubscriptionTier.PRO_PLUS && (
                        <TypographyP>{t('tier.practice')}</TypographyP>
                      )}
                      {tierToUpgrade === SubscriptionTier.PRO && (
                        <TypographyP>{t('tier.pro')}</TypographyP>
                      )}
                    </div>
                    <TypographyCaption className="text-text-secondary">
                      {t('upgradeModal.workWithYourSoftware')}
                    </TypographyCaption>
                  </div>

                  <TypographyP2 className="flex flex-col text-end">
                    <PriceDisplay pricePeriod={period} priceTier={tierToUpgrade} />
                    <span className="text-xs text-text-secondary">
                      {t('upgradeModal.billed', {
                        period: periodDisplay(period),
                      })}
                    </span>
                  </TypographyP2>
                </div>

                <AccordionItem value={tierToUpgrade} className="border-none">
                  <AccordionTrigger className="justify-start py-0 pb-1 text-xs font-normal text-text-tertiary hover:no-underline">
                    {tierToUpgrade === SubscriptionTier.PRO &&
                      t('upgradeModal.everythingInFree')}
                    {tierToUpgrade === SubscriptionTier.PRO_PLUS &&
                      t('upgradeModal.everythingInPro')}
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-y-1 pb-0 text-text-secondary">
                    <p className="flex items-center gap-x-1">
                      <Check className="size-4" strokeWidth={3} />{' '}
                      {t('upgradeModal.integrateWithSoftware')}
                    </p>
                    <p className="flex items-center gap-x-1">
                      <Check className="size-4" strokeWidth={3} />{' '}
                      {t('upgradeModal.collaborateWithTeam')}
                    </p>
                    <p className="flex items-center gap-x-1">
                      <Check className="size-4" strokeWidth={3} />{' '}
                      {t('upgradeModal.supportCreatingTemplates')}
                    </p>
                    <p className="flex items-center gap-x-1">
                      <Check className="size-4" strokeWidth={3} />{' '}
                      {t('upgradeModal.prioritySupport')}
                    </p>
                    <p className="flex items-center gap-x-1">
                      <Check className="size-4" strokeWidth={3} />{' '}
                      {t('upgradeModal.codingAutomation')}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </div>
            </div>
          </div>
        </Card>
      )}

      {userRegionSameAsKindeRegion ? (
        <TypographyP2 className="text-text-secondary">
          <Trans
            i18nKey="upgradeModal.currentLoggedIn"
            defaults="You're currently logged in as {{email}} which is on a <plan></plan> plan."
            values={{
              email: userData?.email || '',
            }}
            components={{
              plan: <ProductName priceTier={userTier} />,
            }}
          />
        </TypographyP2>
      ) : (
        <TypographyP2
          className="w-full text-text-secondary"
          data-testid="integration-user-not-same-region"
        >
          <Trans
            i18nKey="common.currentLoggedIn"
            defaults="You're currently logged in as <span>{{email}}</span>"
            values={{
              email: userData?.email || '',
            }}
            components={{
              span: <span className="font-semibold" />,
            }}
          />
          <br />
          {t('upgradeModal.notSameRegion', {
            integration: integrationName || '',
          })}
        </TypographyP2>
      )}

      <div className="flex flex-row justify-between gap-x-2">
        <Button
          size="lg"
          disableAutomaticSvgSizing
          className="w-1/2"
          disabled={isUpgrading}
          variant="secondary"
          onClick={() => void unlinkAndSwitchAccounts()}
        >
          {t('upgradeModal.switchAccounts')}
        </Button>

        <Button
          size="lg"
          disableAutomaticSvgSizing
          className="w-1/2"
          disabled={isUpgrading || !userRegionSameAsKindeRegion}
          variant={tierToUpgrade === SubscriptionTier.PRO_PLUS ? 'pro_plus' : 'pro'}
          onClick={() => {
            widgetLinkAccountStartTrialClicked({
              integrationName,
            });

            onCheckout({ priceTier: tierToUpgrade, period });
          }}
        >
          {isUpgrading
            ? t('upgradeModal.upgrading')
            : tierToUpgrade === SubscriptionTier.PRO_PLUS
              ? freeTrialAvailable
                ? startTrialMessage
                : t('upgradeModal.upgradeToPractice')
              : t('upgradeModal.upgradeToPro')}
        </Button>
      </div>

      {onDismiss && (
        <Button
          size="lg"
          disableAutomaticSvgSizing
          className="h-auto p-0"
          variant="link"
          onClick={onDismiss}
        >
          {t('upgradeModal.cancelWithoutTrial')}
        </Button>
      )}
    </Accordion>
  );
};

const PriceDisplay = ({
  pricePeriod,
  priceTier,
}: {
  pricePeriod: Period;
  priceTier: SubscriptionTier;
}) => {
  const { locale } = useTranslations();
  const { data: products, isPending, isFetched } = useQueryProducts();
  const product = getTierProduct(priceTier, products);
  const durationText = useDurationText();

  const price = getDisplayedPriceFromPlan(
    getPlanFromPeriod(pricePeriod, product),
    'month',
    true,
    locale,
    durationText
  );

  if (isPending || !isFetched) {
    return <Skeleton className="mb-1 h-6 w-full" />;
  }
  return <>{price}</>;
};

const ProductName = ({ priceTier }: { priceTier: SubscriptionTier }) => {
  const { t } = useTranslations();
  const { data: products, isPending } = useQueryProducts();
  const product = getTierProduct(priceTier, products);
  const { data: inReverseTrial } = useQueryUserBillingInfo(
    (data) => data?.is_in_reverse_free_trial
  );

  if (isPending) {
    return <Skeleton />;
  }

  return <>{inReverseTrial || !product ? t('tier.free') : product.name}</>;
};

export default IntegrationTierAccordion;
